import { nextTick } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import { voucherDebug, voucherDebugWarn, voucherDebugError } from './useVoucherDebug'
import { calculateInstallmentAmount as baseCalculateInstallmentAmount } from '~/composable/installmentMath'
import { useCartStore } from '~/stores/useCartStore'
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore'

/**
 * Composable for voucher installment options management
 * Handles installment price calculations, updates, and resets
 *
 * Phase 1: Extracted from VoucherCode.vue to improve separation of concerns
 */
export const useVoucherInstallmentOptions = () => {
  const cartStore = useCartStore()
  const chosenVariantDetailsStore = useChosenVariantDetailsStore()

  const { getCart } = storeToRefs(cartStore)
  const { chosenChannel, chosenInstallment, productDetails } = storeToRefs(chosenVariantDetailsStore)

  // Helper: Find device/accessory item in cart
  const findDeviceItemInCart = (cart) => {
    if (!cart?.cartItemGroupList) {
      return null
    }
    const currentSku = productDetails.value?.chosenVariantSku
    const allItems = cart.cartItemGroupList.flatMap(group => group.cartItems || [])
    if (currentSku) {
      const matchingCurrentItem = allItems.find(item =>
        (item.cartProduct?.productTypeKey === 'device' ||
          item.cartProduct?.productTypeKey === 'accessory') &&
        item.cartProduct?.sku === currentSku
      )
      if (matchingCurrentItem) {
        return matchingCurrentItem
      }
    }
    return allItems.find(item =>
      item.cartProduct?.productTypeKey === 'device' ||
      item.cartProduct?.productTypeKey === 'accessory'
    ) || null
  }

  // Helper: Find subscription item in cart
  const findSubscriptionItemInCart = (cart) => {
    const cartItemGroup = cart?.cartItemGroupList?.find(group =>
      group.cartItems?.some(item =>
        item.cartProduct?.productTypeKey === 'subscription' ||
        item.cartProduct?.productTypeKey === 'mobile'
      )
    )
    if (!cartItemGroup) {
      return null
    }
    return cartItemGroup.cartItems.find(item =>
      item.cartProduct?.productTypeKey === 'subscription' ||
      item.cartProduct?.productTypeKey === 'mobile'
    ) || null
  }

  // Helper: Get subscription price from line item
  const getSubscriptionPriceFromLineItem = (cart) => {
    const subscriptionItem = findSubscriptionItemInCart(cart)
    const price = subscriptionItem?.cartProduct?.selectedPrice?.lineItemPrice || null
    if (price !== null) {
      voucherDebug('getSubscriptionPriceFromLineItem - Found subscription price:', price)
    }
    return price
  }

  // Helper: Get subscription price fallback
  const getSubscriptionPriceFallback = () => {
    return chosenChannel.value?.productBundlePrice?.bundlePrice || null
  }

  // Helper: Get subscription price from cart installment
  const getSubscriptionPriceFromCartInstallment = (deviceItem) => {
    const cartInstallment = deviceItem?.cartProduct?.selectedPrice?.selectedInstallment
    if (cartInstallment && cartInstallment.monthlyAmountWithSubscription && cartInstallment.amount) {
      const calculatedSubscriptionPrice = cartInstallment.monthlyAmountWithSubscription - cartInstallment.amount
      return calculatedSubscriptionPrice
    }
    return null
  }

  // Helper: Get subscription monthly price (priority order)
  const getSubscriptionMonthlyPrice = (cart) => {
    // Priority 1: From subscription line item
    const priceFromLineItem = getSubscriptionPriceFromLineItem(cart)
    if (priceFromLineItem !== null) {
      return priceFromLineItem
    }

    // Priority 2: From bundlePrice
    const originalSubscriptionPrice = getSubscriptionPriceFallback()
    if (originalSubscriptionPrice !== null) {
      return originalSubscriptionPrice
    }

    // Priority 3: Calculate from cart installment (last resort)
    const deviceItem = findDeviceItemInCart(cart)
    const priceFromInstallment = getSubscriptionPriceFromCartInstallment(deviceItem)
    if (priceFromInstallment !== null) {
      voucherDebugWarn('getSubscriptionMonthlyPrice: Using price from cart installment (may be incorrect)')
      return priceFromInstallment
    }

    voucherDebugError('getSubscriptionMonthlyPrice: Could not find subscription price from any source')
    return null
  }

  // Helper: Calculate installment amount – uses shared math helper
  const calculateInstallmentAmount = (discountedPrice, months) =>
    baseCalculateInstallmentAmount(discountedPrice, months)

  // Helper: Get monthly amount with subscription from cart
  const getMonthlyAmountWithSubscriptionFromCart = (cart) => {
    if (!cart) {
      return null
    }
    const deviceItem = findDeviceItemInCart(cart)
    const cartInstallment = deviceItem?.cartProduct?.selectedPrice?.selectedInstallment
    if (cartInstallment && cartInstallment.monthlyAmountWithSubscription) {
      return cartInstallment.monthlyAmountWithSubscription
    }
    return null
  }

  // Helper: Recalculate monthly amount from components
  const recalculateMonthlyAmountFromComponents = (option, subscriptionMonthlyPrice, discountedPrice) => {
    // Handle cash option (months === 0)
    if (option.months === 0) {
      option.amount = discountedPrice
      option.monthlyAmountWithSubscription = discountedPrice
      return
    }

    // Handle installment options (months > 0)
    const installmentAmount = calculateInstallmentAmount(discountedPrice, option.months)
    if (installmentAmount !== null) {
      option.amount = installmentAmount
      if (subscriptionMonthlyPrice !== null) {
        option.monthlyAmountWithSubscription = subscriptionMonthlyPrice + installmentAmount
      } else {
        option.monthlyAmountWithSubscription = installmentAmount
      }
    }
  }

  // Helper: Recalculate monthly amount from matching option
  const recalculateMonthlyAmountFromMatchingOption = (matchingOption, cart) => {
    const subscriptionMonthlyPrice = getSubscriptionMonthlyPrice(cart || getCart.value)
    const installmentAmount = matchingOption.amount

    if (subscriptionMonthlyPrice !== null && installmentAmount !== null && installmentAmount !== undefined) {
      return subscriptionMonthlyPrice + installmentAmount
    }

    return matchingOption.monthlyAmountWithSubscription
  }

  const hasInstallmentOptions = () =>
    !!chosenChannel.value?.installmentOptions && Array.isArray(chosenChannel.value.installmentOptions)

  const findMatchingOption = (currentMonths) => {
    if (!hasInstallmentOptions()) {
      return null
    }
    return chosenChannel.value.installmentOptions.find(opt => opt.months === currentMonths)
  }

  const updateInstallmentValue = (matchingOption, currentMonths) => {
    const cart = getCart.value
    const recalculatedMonthlyAmount = recalculateMonthlyAmountFromMatchingOption(matchingOption, cart)

    const updatedInstallment = {
      months: currentMonths,
      amount: matchingOption.amount,
      monthlyAmountWithSubscription: recalculatedMonthlyAmount
    }

    chosenInstallment.value = updatedInstallment

    nextTick(() => {
      voucherDebug('After nextTick - chosenInstallment updated:', chosenInstallment.value?.monthlyAmountWithSubscription)
    })
  }

  // Helper: Update chosen installment from options
  const updateChosenInstallmentFromOptions = () => {
    if (!chosenInstallment.value || chosenInstallment.value.months === undefined) {
      return
    }

    const currentMonths = chosenInstallment.value.months
    const matchingOption = findMatchingOption(currentMonths)

    if (matchingOption) {
      updateInstallmentValue(matchingOption, currentMonths)
    }
  }

  // Helper: Update all installment options
  const updateAllInstallmentOptions = (discountedPrice, subscriptionMonthlyPrice) => {
    if (!hasInstallmentOptions()) {
      return
    }

    // Update each option in place
    chosenChannel.value.installmentOptions.forEach(option => {
      recalculateMonthlyAmountFromComponents(option, subscriptionMonthlyPrice, discountedPrice)
    })

    // Create new array reference for Vue reactivity
    chosenChannel.value.installmentOptions = [...chosenChannel.value.installmentOptions]

    // Update chosen installment immediately after updating options
    updateChosenInstallmentFromOptions()
  }

  // Helper: Store original installment options
  const storeOriginalInstallmentOptions = () => {
    if (!hasInstallmentOptions()) {
      return null
    }
    return JSON.parse(JSON.stringify(chosenChannel.value.installmentOptions))
  }

  // Helper: Restore prices from originals
  const restorePricesFromOriginals = (originalInstallmentOptions) => {
    if (!originalInstallmentOptions || !chosenChannel.value?.installmentOptions) {
      return
    }

    chosenChannel.value.installmentOptions.forEach((option, index) => {
      const originalOption = originalInstallmentOptions[index]
      if (originalOption) {
        option.amount = originalOption.amount
        option.monthlyAmountWithSubscription = originalOption.monthlyAmountWithSubscription
      }
    })
  }

  // Helper: Reset installment options to original
  const resetInstallmentOptionsToOriginal = async (originalInstallmentOptions) => {
    if (!originalInstallmentOptions || !chosenChannel.value?.installmentOptions) {
      return
    }

    // Wait for product details to be ready
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 50))

    // Validate originals match current product
    if (originalInstallmentOptions.length !== chosenChannel.value.installmentOptions.length) {
      voucherDebugWarn('Cannot reset - originals do not match current product')
      return
    }

    restorePricesFromOriginals(originalInstallmentOptions)
    chosenChannel.value.installmentOptions = [...chosenChannel.value.installmentOptions]
    updateChosenInstallmentFromOptions()
  }

  // Helper: Get cart installment matching option
  const getCartInstallmentMatchingOption = (cart) => {
    const deviceItem = findDeviceItemInCart(cart)
    if (!deviceItem || !deviceItem.cartProduct?.selectedPrice?.selectedInstallment) {
      return null
    }
    const currentMonths = deviceItem.cartProduct.selectedPrice.selectedInstallment.months
    const matchingOption = chosenChannel.value?.installmentOptions?.find(
      opt => opt.months === currentMonths
    )
    return matchingOption ? { deviceItem, matchingOption, currentMonths } : null
  }

  // Helper: Recalculate cart installment monthly amount
  const recalculateCartInstallmentMonthlyAmount = (matchingOption, cart) => {
    if (matchingOption.months === 0) {
      return matchingOption.amount
    }
    const cartSubscriptionPrice = getSubscriptionMonthlyPrice(cart)
    if (cartSubscriptionPrice !== null && matchingOption.amount !== null && matchingOption.amount !== undefined) {
      return cartSubscriptionPrice + matchingOption.amount
    }
    return matchingOption.monthlyAmountWithSubscription
  }

  const shouldUpdateCartInstallment = (cartInstallment) => {
    return cartInstallment && cartInstallment.months > 0 && !cartInstallment.monthlyAmountWithSubscription
  }

  const updateCartInstallmentAmount = (cartInstallment, installmentAmount, subscriptionMonthlyPrice) => {
    cartInstallment.monthlyAmountWithSubscription = subscriptionMonthlyPrice + installmentAmount
    cartInstallment.amount = installmentAmount
  }

  // Helper: Ensure cart installment has monthly amount
  const ensureCartInstallmentHasMonthlyAmount = (cart, discountedPrice, subscriptionMonthlyPrice) => {
    const deviceItem = findDeviceItemInCart(cart)
    const cartInstallment = deviceItem?.cartProduct?.selectedPrice?.selectedInstallment
    if (!shouldUpdateCartInstallment(cartInstallment)) {
      return
    }
    const installmentAmount = calculateInstallmentAmount(discountedPrice, cartInstallment.months)
    if (installmentAmount && subscriptionMonthlyPrice) {
      updateCartInstallmentAmount(cartInstallment, installmentAmount, subscriptionMonthlyPrice)
    }
  }

  return {
    // Price calculation
    getSubscriptionMonthlyPrice,
    calculateInstallmentAmount,
    getMonthlyAmountWithSubscriptionFromCart,

    // Installment options management
    updateAllInstallmentOptions,
    updateChosenInstallmentFromOptions,
    storeOriginalInstallmentOptions,
    restorePricesFromOriginals,
    resetInstallmentOptionsToOriginal,

    // Cart installment management
    getCartInstallmentMatchingOption,
    recalculateMonthlyAmountFromMatchingOption,
    recalculateCartInstallmentMonthlyAmount,
    ensureCartInstallmentHasMonthlyAmount,

    // Helpers
    findDeviceItemInCart,
    findSubscriptionItemInCart
  }
}
